import {isLoggedIn} from '@github-ui/client-env'
import {Banner} from '@primer/react'
import {useState} from 'react'

import {
  BANNER_CONTENT_WARNINGS,
  type ContentWarningCategory,
  type ContentWarningDefinition,
  type ContentWarningPayload,
  CUSTOM_SUB_CATEGORY,
} from './types'
import {useDismissContentWarning} from './use-dismiss-content-warning'

/** Category slug → definition, built once for O(1) lookups. */
const DEFINITIONS_BY_CATEGORY: ReadonlyMap<ContentWarningCategory, ContentWarningDefinition> = new Map(
  BANNER_CONTENT_WARNINGS.map(definition => [definition.category, definition]),
)

/**
 * Warning banner shown on repositories that Trust & Safety has flagged with a
 * "banner" type content warning. This component is responsible for rendering the
 * warning copy and the dismiss affordance.
 */
export function ContentWarningBanner({
  contentWarning: {category, subCategory, customSubCategory},
  owner,
  repo,
}: ContentWarningPayload) {
  const [dismissed, setDismissed] = useState(false)
  const {dismissContentWarning} = useDismissContentWarning(owner, repo)

  const definition = DEFINITIONS_BY_CATEGORY.get(category)
  if (!definition) return null

  const subCategoryValue =
    subCategory && subCategory !== CUSTOM_SUB_CATEGORY
      ? definition.subCategories?.find(sub => sub.key === subCategory)?.value
      : undefined

  // Complete the subtitle with the recognized sub category copy or custom copy.
  // Non-empty parts are joined with a single space, so the copy
  // doesn't depend on exact leading/trailing whitespace.
  const completion = subCategoryValue ?? customSubCategory ?? ''
  const description = [definition.subtitle, completion].filter(Boolean).join(' ')

  const onDismiss = isLoggedIn()
    ? async () => {
        try {
          if (await dismissContentWarning()) {
            setDismissed(true)
          }
        } catch {
          // Leave the banner in place so the viewer can retry.
        }
      }
    : undefined

  if (dismissed) return null

  return <Banner variant="warning" title={definition.title} description={description} onDismiss={onDismiss} flush />
}
